/**
 * Central definitions for all statistical/domain terms used in the app.
 * Single source of truth for:
 *  - HelpTooltip (hover popups: brief + link)
 *  - Guide page (full explanations, grouped by category)
 *  - Glossary (alphabetical listing)
 *  - Search (filters across term, brief, full)
 *
 * To add new knowledge: add one entry to HELP below.
 * It automatically appears in tooltips, Guide page, glossary, and search.
 */

import { Callout, RuleItem } from "@/components/guide/GuideWidgets";

// ── Types ──────────────────────────────────────────────

export type HelpCategory =
  | "quality-control"
  | "process-capability"
  | "production"
  | "materials"
  | "insights"
  | "equipment"
  | "formulations"
  | "raw-materials-qa"
  | "waste-analysis"
  | "data-explorer"
  | "units";

export interface HelpEntry {
  /** Display name of the term */
  term: string;
  /** URL-safe anchor for deep linking */
  anchor: string;
  /** Category for grouping on the Guide page */
  category: HelpCategory;
  /** One-sentence explanation for tooltip hover */
  brief: string;
  /** Multi-paragraph explanation for the Guide page (plain text, \n\n = paragraph break) */
  full: string;
  /** Optional rich content rendered below `full` on the Guide page (tables, callouts) */
  guideExtra?: () => React.ReactNode;
  /** Sort order within category (lower = earlier). Defaults to 100. */
  order?: number;
}

export interface CategoryMeta {
  id: HelpCategory;
  title: string;
  anchor: string;
  /** Introductory paragraph shown at the top of the category section */
  intro: string;
  order: number;
  /** Optional rich content rendered after the intro */
  guideExtra?: () => React.ReactNode;
}

// ── Categories ─────────────────────────────────────────

export const CATEGORIES: CategoryMeta[] = [
  {
    id: "quality-control",
    title: "Quality Control",
    anchor: "quality-control",
    intro: "The Quality Control page is the analytical heart of the platform. Select a part and measurement type to see an SPC chart showing every test result over time, with statistical boundaries and spec limits overlaid. Below the chart you will find a stats panel (mean, standard deviation, control limits, sample count, in-spec percentage), process alerts from Nelson Rules, and process capability indices (Cp, Cpk).",
    order: 1,
  },
  {
    id: "process-capability",
    title: "Process Capability",
    anchor: "process-capability",
    intro: "Process capability answers a simple question: is this part consistently meeting spec? The key number is Cpk, which accounts for both the spread of results and how well centred the process is between the spec limits.",
    order: 2,
  },
  {
    id: "production",
    title: "Production",
    anchor: "production",
    intro: "The Production page shows moulding output, reject rates, and OEE (Overall Equipment Effectiveness). The daily trend chart shows total parts produced per day. The production table lists individual runs with their part type, quantity, cycle time, and machine assignment.",
    order: 3,
  },
  {
    id: "formulations",
    title: "Formulations",
    anchor: "formulations",
    intro: "The Formulations page is a read-only browser for production recipes. It shows the three-level hierarchy: parts that have formulations, the formulation versions for each part (with ACTIVE or OUTDATED status), and the bill of materials showing the exact material quantities and percentages for each version.",
    order: 4,
  },
  {
    id: "materials",
    title: "Materials",
    anchor: "materials",
    intro: "The Materials page tracks how closely actual material usage matches the recipe targets. Every material in every production run is weighed; the variance table shows the average difference between actual and target percentages in the formulation.",
    order: 5,
  },
  {
    id: "waste-analysis",
    title: "Yield & Waste Analysis",
    anchor: "waste-analysis",
    intro: "The Yield & Waste page tracks production yield and reject rates across moulding operations. It categorises rejects by type (short shot, flash, sink marks, warpage, burn marks, weld lines) and shows yield trends over time. Use the part filter to drill into specific product lines.",
    order: 6,
  },
  {
    id: "raw-materials-qa",
    title: "Raw Materials QA",
    anchor: "raw-materials-qa",
    intro: "The Raw Materials QA page shows SPC control charts for incoming raw material tests — melt flow index, tensile strength, elongation at break, impact strength, moisture content, density, and colour ΔE. Filter by material type (e.g. PA6, ABS, HDPE) and supplier to compare consistency across sources.",
    order: 7,
  },
  {
    id: "insights",
    title: "Insights Dashboard",
    anchor: "insights",
    intro: "The Insights page scans all parts and aggregates quality signals into a single table. Each row shows one part's Nelson Rule alerts, reject rate trends, and Cpk for weight and key dimensions. Use this page as a morning check: sort by the parts needing attention first, then drill into Quality Control for the details.",
    order: 8,
    guideExtra: () => (
      <Callout title="Reading the part health table">
        <ul className="space-y-1 text-xs">
          <li><span className="font-medium text-red-400">Critical</span> — Nelson R1 alerts (point beyond 3σ). Investigate immediately.</li>
          <li><span className="font-medium text-purple-400">Warnings</span> — Nelson R2/R3/R5/R6 pattern alerts. Review when possible.</li>
          <li><span className="font-medium text-amber-400">Elevated Rejects</span> — Reject rate above target threshold. Monitor upcoming runs.</li>
          <li><span className="font-medium">Cpk</span> — Overall process capability rating (see scale in Process Capability section).</li>
        </ul>
      </Callout>
    ),
  },
  {
    id: "equipment",
    title: "Equipment & Calibration",
    anchor: "equipment",
    intro: "The Equipment section tracks injection moulding machines, measurement instruments, and their calibration and maintenance status. Keeping measurement equipment calibrated is essential for ISO 9001 compliance. Each piece of equipment has a calibration history and a calculated status based on the time since last calibration.",
    order: 9,
  },
  {
    id: "data-explorer",
    title: "Data Explorer",
    anchor: "data-explorer",
    intro: "The Data Explorer (under Tools in the sidebar) gives direct access to every table in the PlastiQ database. Browse tables grouped by domain (Production, QA, Materials, Formulations), search and sort any column, and export results to CSV. This is useful for ad-hoc queries and verifying data without needing database access.",
    order: 10,
  },
  {
    id: "units",
    title: "Units of Measurement",
    anchor: "units",
    intro: "",
    order: 11,
  },
];

// ── Help entries ───────────────────────────────────────

export const HELP: Record<string, HelpEntry> = {
  // ─── Quality Control ─────────────────────────────

  spc: {
    term: "SPC",
    anchor: "spc",
    category: "quality-control",
    order: 10,
    brief:
      "Statistical Process Control. Tracks measurement results over time to spot patterns that indicate the process is drifting out of control, before defects occur.",
    full: `Each dot on the chart is one measurement result. The solid centre line is the average (mean). The blue dashed lines are the Upper Control Limit (UCL) and Lower Control Limit (LCL), set at 3 standard deviations from the mean.

If the process is stable, virtually all results fall between UCL and LCL. When points appear outside, or form patterns like long runs above or below the mean, something has changed and should be investigated.

The red dashed lines (if present) are the spec limits. These are the hard pass/fail boundaries from the part's technical specification. Ideally, the control limits sit well inside the spec limits, giving you a safety margin.`,
    guideExtra: () => (
      <Callout title="Chart anatomy">
        <ul className="space-y-1 text-xs">
          <li><span className="font-medium text-blue-400">Blue dots</span> — individual measurement results</li>
          <li><span className="font-medium text-emerald-400">Centre line</span> — average (mean) of all results</li>
          <li><span className="font-medium text-blue-400">Blue dashed</span> — UCL and LCL (3σ boundaries)</li>
          <li><span className="font-medium text-red-400">Red dashed</span> — spec limits (USL and LSL)</li>
          <li><span className="font-medium text-red-400">Red/purple markers</span> — Nelson Rule alerts</li>
        </ul>
      </Callout>
    ),
  },

  ucl: {
    term: "UCL",
    anchor: "ucl",
    category: "quality-control",
    order: 20,
    brief:
      "Upper Control Limit. Set at mean + 3σ on the SPC chart. Points above this line are statistically unusual and trigger a Nelson Rule 1 alert.",
    full: `The Upper Control Limit is the top boundary on an SPC chart, calculated as the mean plus 3 standard deviations. Approximately 99.7% of results should fall below this line if the process is stable.

Important: the UCL is not the same as the upper spec limit (USL). The UCL is a statistical boundary based on what the process actually does. The USL is an engineering requirement for what the part must achieve. You want UCL to be inside the USL.`,
  },

  lcl: {
    term: "LCL",
    anchor: "lcl",
    category: "quality-control",
    order: 21,
    brief:
      "Lower Control Limit. Set at mean - 3σ on the SPC chart. Points below this line are statistically unusual and trigger a Nelson Rule 1 alert.",
    full: `The Lower Control Limit is the bottom boundary on an SPC chart, calculated as the mean minus 3 standard deviations. Points below this line signal something unusual happened in that production run.

As with UCL, the LCL is distinct from the lower spec limit (LSL). The LCL shows what the process normally does. The LSL is the minimum acceptable value. Ideally, LCL sits above LSL with comfortable margin.`,
  },

  nelson_rules: {
    term: "Nelson Rules",
    anchor: "nelson-rules",
    category: "quality-control",
    order: 30,
    brief:
      "A set of pattern-detection rules applied to SPC charts. They catch trends, runs, and shifts that indicate a process is going out of control, even before individual points breach limits.",
    full: `Nelson Rules automatically detect patterns in the SPC chart that suggest the process is drifting or unstable. They are the early warning system: catching problems before they result in out-of-spec parts.

R1 is the most urgent: a single point outside 3σ has only a 0.3% chance of occurring randomly. Rules R2 through R6 detect subtler patterns that are individually less alarming but still warrant investigation if they appear repeatedly.`,
    guideExtra: () => (
      <Callout title="Nelson Rules reference">
        <div className="space-y-2 text-xs">
          <RuleItem code="R1" severity="Critical" text="A single point beyond 3σ (outside UCL or LCL). Something clearly unusual happened in this run — check material, machine settings, or mould condition." />
          <RuleItem code="R2" severity="Warning" text="9 consecutive points on the same side of the mean. The process has shifted up or down — check for gradual barrel temperature drift or material batch change." />
          <RuleItem code="R3" severity="Warning" text="6 consecutive points trending in the same direction. A gradual drift is occurring — possible mould wear, screw wear, or cooling system degradation." />
          <RuleItem code="R5" severity="Warning" text="2 of 3 consecutive points beyond 2σ on the same side. Borderline instability." />
          <RuleItem code="R6" severity="Warning" text="4 of 5 consecutive points beyond 1σ on the same side. A subtle but persistent shift." />
        </div>
      </Callout>
    ),
  },

  sigma: {
    term: "Sigma (σ)",
    anchor: "sigma",
    category: "quality-control",
    order: 40,
    brief:
      "Standard deviation. Measures how spread out results are from the average. Smaller σ means more consistent production.",
    full: `Sigma (σ), or standard deviation, is the fundamental measure of variation. In the context of PlastiQ's QA data, a smaller σ means the moulding process is producing more consistent parts.

The SPC chart divides the area between UCL and LCL into zones based on sigma: 1σ covers about 68% of results, 2σ covers about 95%, and 3σ covers about 99.7%. Nelson Rules use these zones to detect patterns.`,
  },

  in_spec_pct: {
    term: "In-Spec %",
    anchor: "in-spec-pct",
    category: "quality-control",
    order: 50,
    brief:
      "The percentage of measurement results that fall within specification limits. 100% means every result passed; below 95% typically warrants investigation.",
    full: `In-spec percentage is a simple pass/fail metric: how many results fall between the lower spec limit (LSL) and upper spec limit (USL). It is displayed in the stats panel below the SPC chart.

While Cpk gives a more nuanced view of process capability, in-spec % is an intuitive summary. A process with 98% in-spec is passing almost everything but may have occasional outliers. A process at 85% in-spec is producing significant reject volumes and needs attention.`,
  },

  // ─── Process Capability ──────────────────────────

  cpk: {
    term: "Cpk",
    anchor: "cpk",
    category: "process-capability",
    order: 10,
    brief:
      "Process Capability Index. Measures how well results fit within spec limits, accounting for centring. Higher is better: below 1.0 is incapable, above 1.33 is capable, above 1.67 is excellent.",
    full: `Cpk is the key number for answering "is this part consistently meeting spec?" It combines two things: how tight the spread of results is, and how well centred the process is between the upper and lower spec limits.

Cpk is computed as min(Cpu, Cpl), which means it reflects whichever spec limit the process is closest to breaching. A part might have a great Cp (tight spread) but a poor Cpk if the mean has drifted towards one limit.`,
    guideExtra: () => (
      <Callout title="Cpk rating scale">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              <th className="py-1 text-left font-medium">Cpk</th>
              <th className="py-1 text-left font-medium">Rating</th>
              <th className="py-1 text-left font-medium">What it means</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/50">
              <td className="py-1 text-red-400 font-medium">&lt; 1.0</td>
              <td className="py-1 text-red-400">Incapable</td>
              <td className="py-1">Significant proportion of parts outside spec. Needs immediate attention.</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-1 text-amber-400 font-medium">1.0 - 1.33</td>
              <td className="py-1 text-amber-400">Marginal</td>
              <td className="py-1">Most parts pass but little safety margin. Monitor closely.</td>
            </tr>
            <tr className="border-b border-border/50">
              <td className="py-1 text-emerald-400 font-medium">1.33 - 1.67</td>
              <td className="py-1 text-emerald-400">Capable</td>
              <td className="py-1">Good performance with reasonable margin.</td>
            </tr>
            <tr>
              <td className="py-1 text-emerald-400 font-medium">&gt; 1.67</td>
              <td className="py-1 text-emerald-400">Excellent</td>
              <td className="py-1">Very tight, well-centred process. Best in class.</td>
            </tr>
          </tbody>
        </table>
      </Callout>
    ),
  },

  cp: {
    term: "Cp",
    anchor: "cp",
    category: "process-capability",
    order: 20,
    brief:
      "Process Capability (spread only). Measures how much of the spec range the process uses, ignoring centring. Cp = (USL - LSL) / 6σ.",
    full: `Cp measures the potential capability of a process: how tight the results are relative to the spec range. Unlike Cpk, it ignores whether the process is centred. Think of it as "if we perfectly centred this process, how capable would it be?"

A process with Cp = 2.0 but Cpk = 0.8 has a tight spread (good) but is off-centre (fixable). In practice, Cpk is the more important number because it reflects reality, but comparing Cp to Cpk tells you whether the issue is spread or centring.`,
  },

  cpu: {
    term: "Cpu",
    anchor: "cpu",
    category: "process-capability",
    order: 30,
    brief:
      "Upper Capability Index. How far the process mean is from the upper spec limit, in units of 3σ. Higher means more headroom above.",
    full: `Cpu measures how much margin exists between the average result and the upper spec limit. It is calculated as (USL - mean) / 3σ. A value above 1.33 means there is comfortable distance from the upper limit.

When Cpu is the lower of the two (Cpu < Cpl), Cpk equals Cpu, meaning the process is closer to breaching the upper spec limit. This is common when part weight trends high due to over-packing.`,
  },

  cpl: {
    term: "Cpl",
    anchor: "cpl",
    category: "process-capability",
    order: 31,
    brief:
      "Lower Capability Index. How far the process mean is from the lower spec limit, in units of 3σ. Higher means more headroom below.",
    full: `Cpl measures how much margin exists between the average result and the lower spec limit. It is calculated as (mean - LSL) / 3σ. When Cpl is the smaller value, Cpk equals Cpl, meaning the process is closest to breaching the lower spec limit.

For weight measurements on injection moulded parts, a low Cpl is concerning as it may indicate short shots or incomplete fill — the part is trending towards being underweight.`,
  },

  // ─── Production ──────────────────────────────────

  run_number: {
    term: "Run Number",
    anchor: "run-number",
    category: "production",
    order: 10,
    brief:
      "A unique identifier assigned to each production run for traceability throughout the manufacturing and testing process.",
    full: `The run number is the batch tracking identifier used across PlastiQ's production and QA systems. It connects a production run to its material inputs, machine settings, and QA test samples.

On the Production page, run number appears in the production table. On the Quality Control page, run number is shown alongside measurement results to help trace which specific runs produced unusual results.`,
  },

  oee: {
    term: "OEE",
    anchor: "oee",
    category: "production",
    order: 20,
    brief:
      "Overall Equipment Effectiveness. Combines availability, performance, and quality into a single percentage. World-class injection moulding targets 85%+.",
    full: `OEE (Overall Equipment Effectiveness) is the gold-standard production efficiency metric. It is the product of three factors:

Availability = (Run Time / Planned Production Time) — accounts for downtime from mould changes, breakdowns, and material changes.
Performance = (Actual Output / Theoretical Maximum) — accounts for slow cycles, minor stops, and reduced speed.
Quality = (Good Parts / Total Parts) — accounts for rejects, rework, and scrap.

OEE = Availability × Performance × Quality. A machine running 90% of planned time, at 95% of ideal speed, producing 98% good parts has an OEE of 83.8%. World-class injection moulding operations target 85%+.`,
  },

  reject_rate: {
    term: "Reject Rate",
    anchor: "reject-rate",
    category: "production",
    order: 30,
    brief:
      "The percentage of moulded parts that fail quality inspection. Calculated as (rejected parts / total parts) × 100.",
    full: `Reject rate is the percentage of parts that fail quality checks during or after production. In injection moulding, common reject types include short shots, flash, sink marks, warpage, burn marks, and weld lines.

The reject rate is tracked per run, per machine, and per part. A sudden spike may indicate a process change (material batch, temperature drift, mould wear). Sustained high reject rates warrant a root cause investigation — check machine settings, material properties, and mould condition.`,
    guideExtra: () => (
      <Callout title="Common reject types in injection moulding">
        <ul className="space-y-1 text-xs">
          <li><span className="font-medium text-red-400">Short Shot</span> — incomplete fill; part is missing material. Check injection pressure, speed, and material temperature.</li>
          <li><span className="font-medium text-red-400">Flash</span> — excess material at parting line or ejector pins. Check clamp force, mould wear, and injection pressure.</li>
          <li><span className="font-medium text-amber-400">Sink Marks</span> — depressions on thick sections. Increase pack pressure or cooling time.</li>
          <li><span className="font-medium text-amber-400">Warpage</span> — part distortion after ejection. Check cooling uniformity, ejection timing, and residual stress.</li>
          <li><span className="font-medium text-red-400">Burn Marks</span> — dark discolouration from trapped gas. Check venting, injection speed, and barrel temperature.</li>
          <li><span className="font-medium text-amber-400">Weld Lines</span> — visible lines where flow fronts meet. Adjust gate position, melt temperature, or injection speed.</li>
        </ul>
      </Callout>
    ),
  },

  cycle_time: {
    term: "Cycle Time",
    anchor: "cycle-time",
    category: "production",
    order: 40,
    brief:
      "The total time for one injection moulding cycle in seconds. Includes injection, packing, cooling, and ejection phases.",
    full: `Cycle time measures the total duration of one injection moulding cycle from clamp close to part ejection, reported in seconds. It is a key efficiency metric — shorter cycles mean higher output, but too-short cycles can cause quality issues (warpage, short shots, sink marks).

Cycle time is monitored on the SPC chart to detect drift. An increasing trend may indicate cooling issues, mould fouling, or material viscosity changes. Target cycle times are set per part based on the mould and material combination.

The four phases of a typical cycle are: injection (filling the cavity), pack/hold (compensating for shrinkage), cooling (solidifying the part), and ejection (opening the mould and removing the part). Cooling typically accounts for 60-80% of total cycle time.`,
  },

  // ─── Materials ───────────────────────────────────

  abs_pct: {
    term: "Abs %",
    anchor: "abs-pct",
    category: "materials",
    order: 10,
    brief:
      "Absolute percentage variance between target and actual material usage. Ignores whether the variance is over or under; just shows the magnitude.",
    full: `Abs % (absolute percentage) on the Materials page shows how far actual material usage deviates from the target, regardless of direction. A material with an Abs % of 3.2% means it is, on average, 3.2% away from its target quantity in the formulation.

Using absolute values prevents over-usage and under-usage from cancelling each other out. A material that is +5% one run and -5% the next would show 0% average variance, but 5% absolute variance, which better reflects the true inconsistency.

The heatmap colour-codes these values: green (below 1%), amber (1-3%), orange (3-5%), red (above 5%).`,
  },

  correlation_r: {
    term: "Correlation (r)",
    anchor: "correlation-r",
    category: "materials",
    order: 20,
    brief:
      "Pearson correlation coefficient. Ranges from -1 to +1. Measures whether material variance and quality outcomes move together. Values near 0 mean no relationship.",
    full: `The Pearson correlation coefficient (r) on the Materials page measures the linear relationship between material variance and QA outcomes (e.g. part weight, dimensions). It ranges from -1 (perfect negative relationship) to +1 (perfect positive relationship), with 0 meaning no relationship.

When you select a material and a part has QA data, the scatter chart shows whether material variance is associated with changes in quality outcomes. Daily averages are compared, so this is a coarse but useful signal. An |r| above 0.3 suggests the material may be affecting quality.`,
  },

  stddev: {
    term: "Std Dev",
    anchor: "stddev",
    category: "materials",
    order: 30,
    brief:
      "Standard deviation. Shows the typical spread of results around the average. A smaller value means more consistent, predictable results.",
    full: `Standard deviation (Std Dev) quantifies how much individual results vary from the average. On the Quality Control stats panel, it shows the spread of measurement results. On the Materials table, it shows the consistency of material dosing.

A part with a mean weight of 25.0 g and Std Dev of 0.05 g produces very consistent parts. The same part with Std Dev of 0.5 g has wide variation and is likely to produce out-of-spec parts more often.`,
  },

  // ─── Insights ──────────────────────────────────────

  insights_overview: {
    term: "Insights Overview",
    anchor: "insights-overview",
    category: "insights",
    order: 10,
    brief:
      "AI-generated analytics insights that scan all parts and highlight process issues, quality trends, and improvement opportunities.",
    full: `The Insights page uses AI analysis to scan production data and generate actionable observations. It aggregates Nelson Rule alerts, reject rate trends, Cpk values, and material variance data across all parts to identify patterns that might not be obvious when looking at one part at a time.

Insights are ranked by severity and recency. Critical insights (e.g. a part with Cpk below 1.0 or a Nelson R1 alert) appear first. The page refreshes periodically to reflect the latest production data.`,
  },

  // ─── Equipment & Calibration ───────────────────────

  calibration_status: {
    term: "Calibration Status",
    anchor: "calibration-status",
    category: "equipment",
    order: 10,
    brief:
      "The current calibration state of a piece of equipment: OK, Due Soon, or Overdue. Based on the time elapsed since the last calibration record.",
    full: `Each piece of measurement equipment has a calibration status calculated from its most recent calibration date. Equipment without any calibration records shows as "Unknown".

Status is determined by the interval since the last calibration: less than 11 months is OK, 11 to 12 months is Due Soon, and more than 12 months is Overdue. Overdue equipment should be recalibrated before use to maintain ISO 9001 compliance.`,
    guideExtra: () => (
      <Callout title="Status indicators">
        <ul className="space-y-1 text-xs">
          <li><span className="font-medium text-emerald-400">OK</span> — calibrated within the last 11 months. No action needed.</li>
          <li><span className="font-medium text-amber-400">Due Soon</span> — calibrated 11-12 months ago. Schedule recalibration.</li>
          <li><span className="font-medium text-red-400">Overdue</span> — more than 12 months since last calibration. Recalibrate before use.</li>
          <li><span className="font-medium text-muted-foreground">Unknown</span> — no calibration records on file.</li>
        </ul>
      </Callout>
    ),
  },

  equipment_crud: {
    term: "Managing Equipment",
    anchor: "managing-equipment",
    category: "equipment",
    order: 20,
    brief:
      "Add, edit, and view equipment records. Each record tracks the instrument's code, description, manufacturer, model, and calibration history.",
    full: `The Equipment & Calibration section lets you manage measurement instruments (scales, callipers, spectrophotometers, CMMs) and moulding machines. Click any equipment row to expand its calibration history, add new calibration records, or edit the equipment details.

To add new equipment, click the "+ Add Equipment" button and fill in the code and description (the minimum required fields). To record a calibration, expand the equipment row and click "Add Calibration" — enter the date and any relevant value or notes.

The overview cards at the top show total equipment count, how many are overdue, and the most recent calibration date across all instruments.`,
  },

  // ─── Formulations ────────────────────────────────

  formulation_hierarchy: {
    term: "Formulation Hierarchy",
    anchor: "formulation-hierarchy",
    category: "formulations",
    order: 10,
    brief:
      "Parts contain formulation versions, each version contains a bill of materials. Navigate the three-level hierarchy to view recipe details.",
    full: `The Formulations page uses a three-panel master-detail layout. The left panel lists parts that have at least one formulation. Click a part to see its formulation versions in the centre panel. Click a version to see the bill of materials (ingredient list) in the right panel.

Each formulation version has a status: ACTIVE means it is the current production recipe, OUTDATED means it has been superseded. The version label comes from the notes field (e.g. "V3", "V2 - reduced regrind"). The batch count shows how many production runs used that version.`,
  },

  formulation_bom: {
    term: "Bill of Materials",
    anchor: "bill-of-materials",
    category: "formulations",
    order: 20,
    brief:
      "The list of raw materials and their quantities for a formulation version. Percentages show each material's proportion of the total.",
    full: `The bill of materials panel shows every raw material in the selected formulation, with its target quantity and its percentage of the total recipe. For injection moulding formulations, this typically includes the base resin, any fillers or reinforcements (e.g. glass fibre), colourant/masterbatch, and additives (UV stabilisers, flame retardants, processing aids).

For example, an Auto Clip 7A (PA6-GF30) formulation might show: PA6 base resin at 67%, glass fibre at 30%, and colour masterbatch at 3%. The total row at the bottom shows the sum of all material quantities.`,
  },

  formulation_ingredients: {
    term: "Ingredients",
    anchor: "ingredients",
    category: "formulations",
    order: 30,
    brief:
      "The raw material breakdown of a formulation. Each ingredient has a raw material selection and a percentage. Percentages should sum to 100%.",
    full: `The ingredients section lists which raw materials make up the formulation and their percentages. Common ingredient categories for injection moulding include:

Base resins (PA6, PA66, ABS, PC/ABS, HDPE, PP, POM, PMMA), reinforcements (glass fibre, carbon fibre, mineral fillers), colourants (masterbatch, pigment concentrates), and additives (UV stabilisers, flame retardants, mould release agents, anti-static agents).

The total percentage is shown in real-time with colour feedback: green when near 100%, amber when below, red when over. Regrind (recycled material from runners and rejects) may also appear as an ingredient with a specified maximum percentage.`,
  },

  // ─── Raw Materials QA ──────────────────────────────

  raw_material_spc: {
    term: "Raw Material SPC",
    anchor: "raw-material-spc",
    category: "raw-materials-qa",
    order: 10,
    brief:
      "SPC charts for incoming raw material tests. Tracks consistency of material properties like melt flow index, tensile strength, and moisture content.",
    full: `Raw material SPC charts track the consistency of incoming raw materials (PA6, ABS, HDPE, PC/ABS, etc.) over time. The chart shows individual test results with control limits at mean ± 2σ and 3σ.

Unlike finished parts QA, raw material tests may not always have specification limits — quality assessment is often based on statistical control: are results consistent over time? Nelson Rules still apply and flag unusual patterns. A sudden shift in melt flow index, for example, could indicate a supplier batch change that will affect moulding parameters.`,
  },

  melt_flow_index: {
    term: "Melt Flow Index (MFI)",
    anchor: "melt-flow-index",
    category: "raw-materials-qa",
    order: 20,
    brief:
      "Measures how easily a polymer flows when melted, in g/10min. Higher MFI means lower viscosity. Critical for predicting moulding behaviour.",
    full: `Melt Flow Index (MFI), also called Melt Flow Rate (MFR), measures the mass of polymer extruded through a standard die in 10 minutes under specified temperature and load conditions. It is reported in g/10min.

MFI is the most important incoming material test for injection moulding. A material with higher MFI flows more easily but may have lower mechanical properties. Batch-to-batch MFI variation directly affects fill behaviour, pack pressure requirements, and part weight consistency. A shift in MFI may require adjusting injection speed, barrel temperature, or pack pressure to maintain part quality.

Typical ranges vary by material: HDPE 0.5-30 g/10min, PP 5-60 g/10min, ABS 5-50 g/10min, PA6 15-80 g/10min.`,
  },

  tensile_strength: {
    term: "Tensile Strength",
    anchor: "tensile-strength",
    category: "raw-materials-qa",
    order: 30,
    brief:
      "The maximum stress a material can withstand when being pulled apart, measured in MPa. A key mechanical property for structural parts.",
    full: `Tensile strength measures the maximum stress a material specimen can sustain before breaking, tested according to ISO 527. It is reported in megapascals (MPa) and is a critical mechanical property for structural injection moulded parts.

Incoming material tensile strength testing verifies that each batch meets the supplier's datasheet values. Significant drops may indicate material degradation, contamination, or incorrect grade supply. For reinforced materials (e.g. PA6-GF30), tensile strength is particularly sensitive to fibre length and distribution.`,
  },

  elongation_break: {
    term: "Elongation at Break",
    anchor: "elongation-break",
    category: "raw-materials-qa",
    order: 40,
    brief:
      "How much a material can stretch before breaking, expressed as a percentage. Higher values indicate more ductile (flexible) material.",
    full: `Elongation at break measures the percentage increase in length of a specimen at the point of fracture during a tensile test (ISO 527). It indicates material ductility — how much the material deforms before failing.

Brittle materials like glass-fibre-reinforced PA6 have low elongation (3-5%), while ductile materials like HDPE can exceed 500%. A drop in elongation at break compared to the datasheet value may indicate material degradation from moisture absorption, thermal degradation, or contamination.`,
  },

  impact_strength: {
    term: "Impact Strength",
    anchor: "impact-strength",
    category: "raw-materials-qa",
    order: 50,
    brief:
      "Measures a material's resistance to sudden impact, in kJ/m². Tested using Charpy or Izod methods. Critical for parts subject to drops or collisions.",
    full: `Impact strength measures the energy absorbed by a material specimen when struck by a pendulum, tested according to ISO 179 (Charpy) or ISO 180 (Izod). It is reported in kJ/m².

This property is especially important for automotive clips, housings, and any part that must survive drops or impacts. PC/ABS blends, for example, are specifically formulated for high impact strength. Moisture content in hygroscopic materials (PA6, PA66) significantly affects impact strength — poorly dried material can show 50%+ reduction.`,
  },

  moisture_content: {
    term: "Moisture Content",
    anchor: "moisture-content",
    category: "raw-materials-qa",
    order: 60,
    brief:
      "The percentage of water in raw material granules. Critical for hygroscopic polymers (PA, PC, PET) — excess moisture causes splay, bubbles, and reduced properties.",
    full: `Moisture content is measured as a percentage of water by weight in the raw material granules, typically using a halogen moisture analyser or Karl Fischer titration.

Hygroscopic polymers (PA6, PA66, PC, PC/ABS, PET, PMMA) absorb moisture from the atmosphere and must be dried before moulding. Excess moisture causes hydrolytic degradation during processing, resulting in splay marks (silver streaks), bubbles, reduced mechanical properties, and surface defects.

Maximum allowable moisture varies by material: PA6 and PA66 require below 0.1%, PC below 0.02%, ABS below 0.1%. Even non-hygroscopic materials (PP, PE) should be checked if stored in humid conditions, as surface moisture can cause cosmetic defects.`,
  },

  density: {
    term: "Density",
    anchor: "density",
    category: "raw-materials-qa",
    order: 70,
    brief:
      "Mass per unit volume of the raw material, in g/cm³. Used to verify correct material grade and detect contamination or filler variation.",
    full: `Density is measured in g/cm³ and is used to confirm the correct material grade has been supplied. Each polymer type has a characteristic density range: HDPE 0.94-0.97, PP 0.90-0.91, ABS 1.04-1.07, PA6 1.12-1.14, PA6-GF30 1.35-1.38, PC 1.20-1.22.

Density variation beyond the expected range may indicate incorrect grade, contamination with another polymer, or variation in filler/reinforcement content. For filled materials, density is a quick check of filler loading — a lower-than-expected density on PA6-GF30 suggests the glass fibre content is below specification.`,
  },

  colour_delta_e: {
    term: "Colour ΔE",
    anchor: "colour-delta-e",
    category: "raw-materials-qa",
    order: 80,
    brief:
      "Colour difference measurement between the raw material and a reference standard. Lower ΔE values mean closer colour match. Typically tested on masterbatch or coloured resin.",
    full: `Delta E (ΔE) measures the colour difference between a sample and a reference standard using a spectrophotometer. It is tested on coloured raw materials, masterbatch, or pigment concentrates to verify colour consistency between supplier batches.

Values below 1.0 are imperceptible to the human eye. Values between 1.0 and 3.0 are noticeable under close inspection. Values above 3.0 indicate a visible colour shift that will likely be apparent in the moulded parts. For consumer products where colour matching is critical, incoming ΔE testing catches supplier variation before it reaches the moulding floor.`,
  },

  product_type_filter: {
    term: "Material Type Filter",
    anchor: "product-type-filter",
    category: "raw-materials-qa",
    order: 90,
    brief:
      "Filter raw material test results by material type and supplier. For example, PA6 may have types from different suppliers like BASF Ultramid or Lanxess Durethan.",
    full: `Each raw material test record includes a material type field that identifies the specific grade and supplier. For PA6, this might be "BASF Ultramid B3S", "Lanxess Durethan B30S", or another grade.

Filtering by material type lets you compare consistency across suppliers and identify whether quality issues are tied to a specific source. The filter dropdown is populated from the distinct material types in the test data.`,
  },

  // ─── Yield & Waste ────────────────────────────────

  waste_percentage: {
    term: "Waste %",
    anchor: "waste-percentage",
    category: "waste-analysis",
    order: 10,
    brief:
      "The percentage of material or parts lost to waste in a production run. Includes rejects, runners/sprues, and purge material.",
    full: `Waste percentage combines all material losses: rejected parts, runners and sprues (if not using hot runners), startup/purge waste, and material lost to colour or material changeovers.

The trend chart shows waste percentage over time with adjustable time buckets (daily, weekly, monthly), helping identify periods of higher or lower waste. Target waste rates vary by part complexity and runner system — hot runner moulds typically achieve below 2%, while cold runner moulds may have 5-15% runner waste that can be partially recovered as regrind.`,
  },

  reject_types: {
    term: "Reject Classification",
    anchor: "reject-types",
    category: "waste-analysis",
    order: 20,
    brief:
      "Categorises rejected parts by defect type (short shot, flash, sink marks, warpage, burn marks, weld lines) to identify root causes.",
    full: `The reject classification breakdown shows which defect types are contributing most to waste. This Pareto-style analysis helps prioritise corrective actions — fixing the top defect type gives the biggest yield improvement.

Common reject types and their typical root causes:
- Short shot: insufficient material fill (low injection pressure, cold material, blocked gate)
- Flash: material escaping the mould cavity (excessive pressure, worn parting line, insufficient clamp force)
- Sink marks: surface depressions on thick sections (insufficient pack pressure, short pack time, hot melt)
- Warpage: part distortion (uneven cooling, high mould temperature differential, premature ejection)
- Burn marks: dark discolouration (trapped air, excessive injection speed, degraded material)
- Weld lines: visible lines where flow fronts meet (cold melt, poor venting, gate location)`,
  },

  yield_rate: {
    term: "Yield Rate",
    anchor: "yield-rate",
    category: "waste-analysis",
    order: 30,
    brief:
      "The percentage of good parts produced from total parts moulded. Yield = (good parts / total parts) × 100. Higher is better.",
    full: `Yield rate is the inverse of reject rate: it shows what percentage of moulded parts pass quality inspection. A yield rate of 97% means 3 out of every 100 parts are rejected.

The yield rate KPI is tracked per run, per machine, per part, and across the entire plant. Injection moulding typically targets yield rates above 98%. Yield below 95% on any part warrants a root cause investigation into machine settings, material properties, and mould condition.

The over/under usage KPI cards show material waste in kilograms — over-usage (more material consumed than target) increases costs, while under-usage may indicate incomplete shots or metering issues.`,
  },

  // ─── Data Explorer ─────────────────────────────────

  explorer_tables: {
    term: "Table Browser",
    anchor: "table-browser",
    category: "data-explorer",
    order: 10,
    brief:
      "Browse all database tables grouped by domain (Production, QA, Materials, Formulations). Select a table to view its data with pagination, sorting, and search.",
    full: `The left panel of the Data Explorer lists every table in the PlastiQ database, grouped by domain. Each table shows its name and row count. Click a table to load its data in the main panel.

The data view supports column sorting (click any header), text search across all columns, and pagination for large tables.

Tables are read-only in the Data Explorer — it is a viewing and export tool, not an editor.`,
  },

  csv_export: {
    term: "CSV Export",
    anchor: "csv-export",
    category: "data-explorer",
    order: 20,
    brief:
      "Download the current table view (with any active search filter) as a CSV file. Useful for further analysis in Excel or Google Sheets.",
    full: `The export button in the Data Explorer downloads the currently displayed data as a CSV file. If you have a search filter active, only the matching rows are exported. The filename includes the table name for easy identification.

This is useful for ad-hoc analysis: find the data you need in the Explorer, apply a search filter if needed, then export to CSV for pivot tables, charting, or sharing with colleagues who do not have platform access.`,
  },

  // ─── Units ───────────────────────────────────────

  grams: {
    term: "Grams (g)",
    anchor: "grams",
    category: "units",
    order: 5,
    brief:
      "The unit of measurement for part weight. Consistent weight indicates stable fill and packing in the injection moulding process.",
    full: `Grams (g) is the standard unit for measuring injection moulded part weight. Weight is the most commonly monitored QA metric because it correlates strongly with fill completeness, packing pressure, and material consistency.

Typical ranges at PlastiQ vary by part: Auto Clip 7A (PA6-GF30) ~12 g, Medical Housing 3mm (PC/ABS) ~85 g, Cable Gland M20 (PA66) ~18 g, Bottle Cap 30mm (HDPE) ~3 g, Gear Wheel 50T (POM) ~45 g.`,
  },

  millimetres: {
    term: "Millimetres (mm)",
    anchor: "millimetres",
    category: "units",
    order: 6,
    brief:
      "The unit for dimensional measurements (length, width, height, diameter). Tolerances are typically ±0.05 to ±0.5 mm depending on part criticality.",
    full: `Millimetres (mm) is the standard unit for all dimensional measurements on injection moulded parts. Each part has nominal dimensions with upper and lower tolerances specified in the part drawing.

Tolerance ranges vary by application: precision engineering parts (gears, connectors) may require ±0.05 mm, while general-purpose parts (caps, fittings) may allow ±0.3 mm. Dimensional SPC charts track trends that may indicate mould wear, thermal drift, or material shrinkage changes.`,
  },

  seconds: {
    term: "Seconds (s)",
    anchor: "seconds",
    category: "units",
    order: 7,
    brief:
      "The unit for cycle time measurement. Total cycle time includes injection, pack/hold, cooling, and ejection phases.",
    full: `Seconds (s) is the unit for cycle time in injection moulding. Typical cycle times range from 5 seconds for thin-walled packaging to over 60 seconds for thick-walled technical parts.

Cycle time breakdown for a typical part: injection 1-3s, pack/hold 3-10s, cooling 10-40s, mould open/close and ejection 2-5s. Cooling is almost always the longest phase and the primary target for cycle time reduction.`,
  },

  mpa: {
    term: "MPa",
    anchor: "mpa",
    category: "units",
    order: 8,
    brief:
      "Megapascals. Unit for mechanical properties (tensile strength, flexural strength) and process pressures (injection pressure, pack pressure).",
    full: `Megapascals (MPa) is used for two purposes in PlastiQ: material mechanical properties (tensile strength, flexural strength, compressive strength from raw material testing) and process pressures (injection pressure, pack/hold pressure from machine settings).

For raw material properties: PA6-GF30 tensile strength ~180 MPa, ABS ~45 MPa, HDPE ~25 MPa, POM ~65 MPa. For process pressures: injection pressure 50-150 MPa, pack pressure 30-80 MPa (typically 50-80% of injection pressure).`,
  },

  delta_e_unit: {
    term: "ΔE (Colour Difference)",
    anchor: "delta-e",
    category: "units",
    order: 9,
    brief:
      "A dimensionless colour difference measurement. Lower values mean closer colour match to the reference standard.",
    full: `Delta E (ΔE) is a dimensionless number representing the perceived colour difference between a sample and a reference standard. It is calculated from L*a*b* colour space measurements using a spectrophotometer.

Values below 1.0 are imperceptible to the human eye. Values between 1.0 and 2.0 are perceptible through close inspection. Values between 2.0 and 3.5 are noticeable at a glance. Values above 3.5 indicate a clear colour mismatch. For automotive and consumer electronics parts, the acceptable ΔE is typically below 1.5.`,
  },

  spec_limits: {
    term: "Spec Limits",
    anchor: "spec-limits",
    category: "units",
    order: 10,
    brief:
      "The acceptable range for measurement results, defined by the part specification. Results outside spec limits are failures.",
    full: `Spec limits (specification limits) define the acceptable range for each measurement result. They come from the part's technical drawing or product specification and represent hard pass/fail boundaries.

On the SPC chart, spec limits appear as red dashed lines. The control limits (UCL/LCL, blue dashed lines) show what the process actually does. Ideally, control limits sit well inside spec limits, giving you margin. When they are close together or overlapping, the process is marginal and rejects are likely.`,
  },

  usl: {
    term: "USL",
    anchor: "usl",
    category: "units",
    order: 20,
    brief:
      "Upper Spec Limit. The maximum acceptable value for a measurement, defined by the part specification. Shown as a red dashed line on SPC charts.",
    full: `The Upper Spec Limit is the engineering boundary: results above this value fail the specification. On SPC charts, it appears as a red dashed line. For weight measurements, exceeding the USL means the part is overweight (possible over-packing or flash).

Not all measurements have both a USL and LSL. Some only have a lower limit (minimum tensile strength) or only an upper limit (maximum moisture content).`,
  },

  lsl: {
    term: "LSL",
    anchor: "lsl",
    category: "units",
    order: 21,
    brief:
      "Lower Spec Limit. The minimum acceptable value for a measurement. For weight, this is the minimum grams the part must achieve.",
    full: `The Lower Spec Limit is the minimum acceptable value. For part weight, falling below the LSL may indicate a short shot or incomplete fill. For dimensional measurements, it means the part is undersized.

On the SPC chart, the LSL appears as a red dashed line at the bottom. The gap between the LCL and LSL represents the safety margin of the process.`,
  },

  g_per_10min: {
    term: "g/10min",
    anchor: "g-per-10min",
    category: "units",
    order: 25,
    brief:
      "Grams per 10 minutes. The unit for Melt Flow Index (MFI/MFR). Higher values mean the polymer flows more easily when melted.",
    full: `Grams per 10 minutes (g/10min) is the standard unit for Melt Flow Index testing (ISO 1133). The test measures how much polymer is extruded through a standard die under specified temperature and load conditions in 10 minutes.

The test conditions (temperature and load) vary by material type: PP at 230°C/2.16 kg, HDPE at 190°C/2.16 kg, ABS at 220°C/10 kg, PA6 at 235°C/2.16 kg. Always check that incoming material test results use the correct test conditions for the grade.`,
  },

  kj_per_m2: {
    term: "kJ/m²",
    anchor: "kj-per-m2",
    category: "units",
    order: 26,
    brief:
      "Kilojoules per square metre. The unit for impact strength (Charpy or Izod). Higher values mean better resistance to sudden impact.",
    full: `Kilojoules per square metre (kJ/m²) is the standard unit for impact strength testing. The value represents the energy absorbed by the specimen per unit cross-sectional area at the notch.

Impact strength values vary widely: unreinforced PA6 can exceed 80 kJ/m² (very tough), while glass-fibre-reinforced PA6-GF30 drops to around 10-15 kJ/m² (brittle). PC/ABS blends are specifically engineered for high impact strength, typically 30-50 kJ/m².`,
  },

  // ─── Process-specific terms ─────────────────────────

  cushion_position: {
    term: "Cushion Position",
    anchor: "cushion-position",
    category: "quality-control",
    order: 60,
    brief:
      "The amount of material remaining in front of the screw after injection, in mm. Ensures consistent packing — too small risks short shots, too large wastes material.",
    full: `Cushion position (also called residual cushion) is the distance the screw has left to travel at the end of the injection and pack/hold phase. It represents the material "buffer" that ensures the pack pressure can be maintained to compensate for shrinkage as the part cools.

A consistent cushion position indicates stable shot-to-shot repeatability. The target is typically 3-10 mm depending on machine and shot size. If the cushion approaches zero, pack pressure cannot be maintained and the part may show sink marks or be underweight. If the cushion is too large, material is wasted and may degrade from excessive residence time.

On the SPC chart, cushion position is monitored to detect screw wear, check ring wear, or material viscosity changes.`,
  },

  pack_pressure: {
    term: "Pack/Hold Pressure",
    anchor: "pack-pressure",
    category: "quality-control",
    order: 70,
    brief:
      "The pressure applied after injection to compensate for material shrinkage as the part cools. Critical for part weight, sink marks, and dimensional accuracy.",
    full: `Pack/hold pressure (also called holding pressure or secondary pressure) is applied after the cavity is filled to force additional material into the mould as the plastic shrinks during cooling. It is typically set at 50-80% of injection pressure.

Pack pressure directly affects part weight, dimensional accuracy, and surface quality. Too low: sink marks, voids, underweight parts. Too high: flash, overpacking, internal stress, difficult ejection. The pack pressure profile (pressure vs time) is one of the most critical process parameters in injection moulding.

On the SPC chart, pack pressure consistency indicates stable hydraulic system performance. Variation may indicate hydraulic oil temperature changes, valve wear, or proportional valve calibration drift.`,
  },

  part_weight: {
    term: "Part Weight",
    anchor: "part-weight",
    category: "quality-control",
    order: 55,
    brief:
      "Part weight in grams. The primary QA metric for injection moulded parts — consistent weight indicates stable fill and packing.",
    full: `Part weight is the primary quality measure for injection moulded parts. Each part is weighed after moulding and compared against the nominal weight and tolerance defined in the part specification.

Weight is the most sensitive indicator of process stability. Variations in weight can indicate changes in material viscosity, barrel temperature, pack pressure, or cushion position. The SPC chart tracks weight trends over time to detect drift before it causes out-of-spec parts.

Weight correlates strongly with other quality attributes: an underweight part likely has short shots or incomplete fill, while an overweight part may have flash or be overpacked.`,
  },
};

// ── Utility functions ──────────────────────────────────

/** Get a help entry by key */
export function getHelp(key: string): HelpEntry | undefined {
  return HELP[key];
}

/** All entries sorted alphabetically by term (for glossary) */
export function glossaryEntries(): HelpEntry[] {
  return Object.values(HELP).sort((a, b) =>
    a.term.localeCompare(b.term, "en-AU")
  );
}

/** Get all entries for a category, sorted by order */
export function entriesByCategory(category: HelpCategory): HelpEntry[] {
  return Object.values(HELP)
    .filter((e) => e.category === category)
    .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
}

/** Get categories in display order */
export function orderedCategories(): CategoryMeta[] {
  return [...CATEGORIES].sort((a, b) => a.order - b.order);
}
